import { useState } from 'react';
import { Edit, Eye, EyeOff } from 'lucide-react';

interface ProfileData {
  userName: string;
  city: string;
  shopName: string;
  governorate: string;
  address: string;
  addressOptional: string;
  name: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
}

export default function ProfileScreen() {
  const [isEditing, setIsEditing] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    userName: 'Test 1',
    city: 'Pakistan',
    shopName: 'Kurdistan Market',
    governorate: 'Kurdistan',
    address: 'Pakistan',
    addressOptional: '',
    name: 'Test Deve',
    email: 'Test@example.com',
    phone: '0774 XXX XXXX',
    password: '1224893',
    passwordConfirmation: '**********'
  });

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    console.log('Saving profile data:', profileData);
    setIsEditing(false);
  };

  // const handleCancel = () => {
  //   setProfileData({
  //     userName: 'Test 1',
  //     city: 'Pakistan',
  //     shopName: 'Kurdistan Market',
  //     governorate: 'Kurdistan',
  //     address: 'Pakistan',
  //     addressOptional: '',
  //     name: 'Test Deve',
  //     email: 'Test@example.com',
  //     phone: '0774 XXX XXXX',
  //     password: '1224893',
  //     passwordConfirmation: '**********'
  //   });
  //   setIsEditing(false);
  // };

  return (
    <div className="mx-8">
      <div className="rounded-[16px] border border-[#C8D0E2] bg-white px-10 py-10 shadow-[0_2px_8px_rgba(25,42,78,0.05)]">
        <div className="flex items-start gap-10">
          <div className="flex min-w-[150px] flex-col items-center gap-4">
            <div className="flex h-[130px] w-[130px] items-center justify-center overflow-hidden rounded-full bg-teal-600 ring-4 ring-white shadow-md">
              <img
                src="/img/profile-placeholder.jpg"
                alt="Profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  img.style.display = 'none';
                  const sibling = img.nextElementSibling as HTMLElement | null;
                  if (sibling) sibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full bg-teal-500 flex items-center justify-center text-white text-2xl font-bold" style={{ display: 'none' }}>
                TD
              </div>
            </div>

            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#EEEEFF] px-5 py-2.5 text-[14px] font-bold text-[#F6C600] hover:bg-[#E4E4FA] transition-colors"
            >
              <Edit className="w-4 h-4" /> Update Profile
            </button>
          </div>

          <div className="flex-1 space-y-6">
            <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                User Name
              </label>
              <input
                type="text"
                value={profileData.userName}
                onChange={(e) => handleInputChange('userName', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                City
              </label>
              <input
                type="text"
                value={profileData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Shop Name
              </label>
              <input
                type="text"
                value={profileData.shopName}
                onChange={(e) => handleInputChange('shopName', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Governorate
              </label>
              <input
                type="text"
                value={profileData.governorate}
                onChange={(e) => handleInputChange('governorate', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Address
              </label>
              <input
                type="text"
                value={profileData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Address (optional)
              </label>
              <input
                type="text"
                value={profileData.addressOptional}
                onChange={(e) => handleInputChange('addressOptional', e.target.value)}
                disabled={!isEditing}
                placeholder="Address (optional)"
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] placeholder:text-[#A3AEC7] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Name
              </label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Email
              </label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Phone: Kurdistan
              </label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={!isEditing}
                className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={profileData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  disabled={!isEditing}
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 pr-10 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            
            <div>
              <label className="mb-2 block text-[14px] font-semibold text-[#111318]">
                Password Confirmation
              </label>
              <div className="relative">
                <input
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  value={profileData.passwordConfirmation}
                  onChange={(e) => handleInputChange('passwordConfirmation', e.target.value)}
                  disabled={!isEditing}
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 pr-10 text-[14px] text-[#6A7698] focus:border-[#BCC8E5] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5] disabled:bg-[#FAFBFF]"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswordConfirmation ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-center pt-6">
              <button
                onClick={handleSave}
                className="h-11 w-[148px] rounded-md bg-[#5B5BF8] text-[16px] font-medium text-white transition-colors hover:bg-[#4f4ee4]"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}